<template>
  <v-container fluid class="annual-view">
    <v-card class="ui-module-card ui-module-fade-in-up evaluacion-anual-shell" flat>
      <v-card-title class="ui-module-titlebar text-h5">Evaluación de Desempeño Anual</v-card-title>

      <v-card-text>
        <v-btn text @click="$router.go(-1)" class="mb-2">
          <v-icon>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <p class="view-subtitle">Captura y seguimiento de reactivos de evaluación para personal operativo y mandos.</p>

        <v-row>
          <v-col v-if="loadingPersonal" cols="12" align="center">
            <div class="d-flex flex-column align-center loading-block">
              <v-progress-circular indeterminate color="accent" size="32"></v-progress-circular>
              <h4 class="mt-4 loading-text">Cargando información de personal...</h4>
            </div>
          </v-col>
          <template v-else>
            <v-col cols="12">
              <div class="section-shell">
                <DatosGeneralesForm
                  :personal="personalEvaluado"
                  :tipoEvaluacion.sync="tipoEvaluacion"
                  :fechaAplicacion.sync="fechaAplicacion"
                  :idEvaluador.sync="idEvaluador"
                />
              </div>
            </v-col>
            <v-col v-if="funcionesStatus === ACTION_STATUS.LOADING" cols="12">
              <div class="d-flex flex-column align-center loading-block">
                <v-progress-circular indeterminate color="accent" size="32"></v-progress-circular>
                <h4 class="mt-4 loading-text">Cargando listado de funciones...</h4>
              </div>
            </v-col>
            <v-col v-if="funcionesStatus === ACTION_STATUS.SUCCEEDED" cols="12">
              <div class="section-shell">
                <CumplimientoFuncionesForm
                  v-for="rubro in rubrosWithObjetivos"
                  :key="rubro.id"
                  :rubro="rubro"
                  :reactivos.sync="reactivos"
                  :frecuencias="frecuencias"
                  :tipoEvaluacion="tipoEvaluacion"
                />
              </div>
              <div class="mt-4 d-flex justify-end align-baseline eval-actions">
                <BaseSelect
                  v-model="estatusEvaluacion"
                  label="Estatus"
                  class="mr-2 status-select"
                  style="max-width: 320px"
                  :items="estatusOptions"
                />
              </div>
              <div class="mt-4 d-flex justify-end align-baseline eval-actions">
                <TextButton
                  class="save-button"
                  :loading="creatingEvaluacion"
                  :disabled="!allReactivosAreFilled || creatingEvaluacion"
                  @click="saveEvaluacion"
                >
                  Guardar evaluación
                </TextButton>
              </div>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import DatosGeneralesForm from '@/components/desarrollo_policial/evaluacion-anual/DatosGeneralesForm.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CumplimientoFuncionesForm from '@/components/desarrollo_policial/evaluacion-anual/CumplimientoFuncionesForm.vue'
import EvaluacionAnualApiRepository from '@/modules/evaluacion-anual/infrastructure/EvaluacionAnualApiRepository'
import GetRubros from '@/modules/evaluacion-anual/application/GetRubros'
import RUBRO_OBJETIVO_EVACOM from '@/modules/evaluacion-anual/domain/RUBRO_OBJETIVO_EVACOM'
import RUBRO_OBJETIVO_EVACOM_MANDOS from '@/modules/evaluacion-anual/domain/RUBRO_OBJETIVO_EVACOM_MANDOS'
import GetReactivos from '@/modules/evaluacion-anual/application/GetReactivos'
import GetFrecuencias from '@/modules/evaluacion-anual/application/GetFrecuencias'
import { ACTION_STATUS } from '@/const'
import TextButton from '@/components/base/TextButton.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import CreateEvaluacionAnual from '@/modules/evaluacion-anual/application/CreateEvaluacionAnual'
import { TIPOS_EVALUACION_ANUAL } from '@/modules/evaluacion-anual/domain/TIPOS_EVALUACION_ANUAL'

export default {
  components: {
    DatosGeneralesForm,
    CumplimientoFuncionesForm,
    TextButton,
    BaseSelect,
  },

  props: {
    idPersonalEvaluado: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      ACTION_STATUS,
      loadingPersonal: true,
      tipoEvaluacion: null,
      fechaAplicacion: '',
      idEvaluador: null,
      rubros: [],
      reactivos: [],
      frecuencias: [],
      funcionesStatus: ACTION_STATUS.IDLE,
      estatusEvaluacion: null,
      estatusOptions: [
        {
          id: 2,
          title: 'Evaluación en Proceso',
        },
        {
          id: 3,
          title: 'Evaluación Finalizada',
        },
        {
          id: 1,
          title: 'Evaluación Pendiente',
        },
      ],
      creatingEvaluacion: false,
    }
  },

  computed: {
    ...mapGetters({ idPersonalUsuario: 'getIdPersonal' }),
    ...mapGetters('personal', { personalEvaluado: 'getPersonalSingle' }),

    rubrosWithObjetivos: function () {
      return this.rubros.map((rubro) => {
        const objetivo =
          this.tipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM
            ? RUBRO_OBJETIVO_EVACOM[rubro.id]
            : RUBRO_OBJETIVO_EVACOM_MANDOS[rubro.id]

        return { ...rubro, objetivo: objetivo ?? 'N/A' }
      })
    },

    allReactivosAreFilled: function () {
      return this.reactivos.every(({ calificacion }) => !!calificacion)
    },
  },

  watch: {
    async tipoEvaluacion(newValue, oldValue) {
      if (newValue !== oldValue) {
        try {
          this.funcionesStatus = ACTION_STATUS.LOADING
          this.reactivos = await new GetReactivos(EvaluacionAnualApiRepository).execute(newValue)
          this.funcionesStatus = ACTION_STATUS.SUCCEEDED
        } catch (error) {
          this.funcionesStatus = ACTION_STATUS.FAILED
        }
      }
    },
  },

  async mounted() {
    this.findPersonal()
    this.rubros = await new GetRubros(EvaluacionAnualApiRepository).execute()
    this.frecuencias = await new GetFrecuencias(EvaluacionAnualApiRepository).execute()
  },

  methods: {
    ...mapActions('personal', ['fetchPersonalById']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    async findPersonal() {
      try {
        await this.fetchPersonalById(this.idPersonalEvaluado)
      } catch (error) {
        console.error('Error fetching personal data:', error)
      } finally {
        this.loadingPersonal = false
      }
    },

    async saveEvaluacion() {
      try {
        this.creatingEvaluacion = true

        const usecase = new CreateEvaluacionAnual(EvaluacionAnualApiRepository)
        await usecase.execute({
          idPersonalEvaluado: this.idPersonalEvaluado,
          idPersonalEvaluador: this.idEvaluador,
          idSupervisor: this.idPersonalUsuario,
          estatus: this.estatusEvaluacion,
          fecha: this.fechaAplicacion,
          reactivos: this.reactivos,
          tipoEvaluacion: this.tipoEvaluacion,
        })

        this.SET_SNACKBAR_MESSAGE('Evaluación guardada correctamente.')
        this.$router.push('/desarrollo_profesional/personal')
      } catch (error) {
        console.error('Error saving evaluacion:', error)
        this.SET_SNACKBAR_MESSAGE('No fue posible guardar la evaluación. Intente nuevamente.')
      } finally {
        this.creatingEvaluacion = false
      }
    },
  },
}
</script>

<style scoped>
.view-title {
  color: #e0e0e0;
  font-weight: 800;
  letter-spacing: 0.2px;
  margin-bottom: 6px;
}

.view-subtitle {
  color: rgba(224, 224, 224, 0.7);
  margin-bottom: 0;
}

.section-shell {
  border: 1px solid rgba(var(--v-secondary-base-rgb, 255, 165, 2), 0.2);
  border-radius: 20px;
  padding: 8px;
  background: rgba(30, 30, 30, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.loading-block {
  border: 1px solid rgba(var(--v-secondary-base-rgb, 255, 165, 2), 0.18);
  border-radius: 16px;
  padding: 28px;
  background: rgba(30, 30, 30, 0.22);
}

.loading-text {
  color: #e0e0e0;
  font-weight: 600;
}

.eval-actions {
  gap: 10px;
}

::v-deep .status-select .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb, 255, 165, 2), 0.25) !important;
  background: rgba(20, 20, 20, 0.45) !important;
}

::v-deep .status-select .v-label {
  color: rgba(224, 224, 224, 0.75) !important;
}

::v-deep .status-select.v-input--is-focused .v-label {
  color: var(--v-secondary-base) !important;
}

.save-button {
  box-shadow: 0 6px 20px rgba(var(--v-secondary-base-rgb, 255, 165, 2), 0.3);
}

.evaluacion-anual-shell {
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
}

::v-deep .evaluacion-anual-shell.v-card {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

::v-deep .evaluacion-anual-shell.v-card:hover {
  transform: none !important;
  box-shadow: none !important;
}
</style>
